import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { fetchSubscription } from "../services/api";
import "../style/Nav.css";
import logo from "../assets/logo.png";

type HeaderProfileData = {
  displayName?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatarUrl?: string | null;
};

type UserPlan = "free" | "pro" | "premium";

const CURRENT_AVATAR_KEY = "qfo-current-profile-avatar";
const CURRENT_PROFILE_KEY = "qfo-current-profile-data";
const PROFILE_UPDATED_EVENT = "qfo-profile-updated";

/**
 * Determines user tier from Stripe price ID.
 * Falls back to free if planId is missing or invalid.
 */
const detectTierFromPlanId = (planId?: string | null): UserPlan => {
  const id = String(planId || "").toLowerCase();

  if (!id || id.includes("free") || id === "price_free") return "free";

  const PRO_ID = (
    process.env.REACT_APP_STRIPE_PRICE_PRO || "price_1S8tsnQTtrbKnENdYfv6azfr"
  ).toLowerCase();

  const PREMIUM_ID = (
    process.env.REACT_APP_STRIPE_PRICE_PREMIUM ||
    "price_1SB17tQTtrbKnENdT7aClaEe"
  ).toLowerCase();

  if (id === PRO_ID || id.includes("pro")) return "pro";
  if (id === PREMIUM_ID || id.includes("premium")) return "premium";

  return "free";
};

const getInitial = (
  profile: HeaderProfileData | null,
  fallbackEmail?: string
): string => {
  return (
    profile?.firstName?.[0] ||
    profile?.displayName?.[0] ||
    profile?.username?.[0] ||
    profile?.email?.[0] ||
    fallbackEmail?.[0] ||
    "U"
  ).toUpperCase();
};

const getStoredHeaderProfile = (): HeaderProfileData | null => {
  try {
    const storedProfile = localStorage.getItem(CURRENT_PROFILE_KEY);
    const storedAvatar = localStorage.getItem(CURRENT_AVATAR_KEY);

    const parsedProfile = storedProfile ? JSON.parse(storedProfile) : {};

    return {
      ...parsedProfile,
      avatarUrl: storedAvatar || null,
    };
  } catch {
    return null;
  }
};

const getNavLinkStyle = (isActive: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "7px 14px",
  borderRadius: "10px",
  textDecoration: "none",
  fontWeight: 600,
  fontSize: "13px",
  color: isActive ? "#334155" : "#64748b",
  background: isActive ? "#e2e8f0" : "transparent",
  transition: "background 0.2s, color 0.2s, transform 0.15s ease",
});

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerProfile, setHeaderProfile] = useState<HeaderProfileData | null>(
    getStoredHeaderProfile()
  );

  const { user } = useAuth();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Keep navbar avatar/profile data synced with ProfilePage.
   * ProfilePage dispatches qfo-profile-updated after the user changes/removes avatar.
   */
  useEffect(() => {
    if (!user) {
      setHeaderProfile(null);
      return;
    }

    const storedProfile = getStoredHeaderProfile();

    setHeaderProfile({
      email: user.email || storedProfile?.email,
      ...storedProfile,
    });
  }, [user]);

  useEffect(() => {
    const handleProfileUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{
        avatarUrl: string | null;
        profile?: HeaderProfileData;
      }>;

      setHeaderProfile((prev) => ({
        ...prev,
        ...customEvent.detail.profile,
        avatarUrl: customEvent.detail.avatarUrl,
      }));
    };

    const handleStorageUpdate = () => {
      setHeaderProfile(getStoredHeaderProfile());
    };

    window.addEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdate);
    window.addEventListener("storage", handleStorageUpdate);

    return () => {
      window.removeEventListener(PROFILE_UPDATED_EVENT, handleProfileUpdate);
      window.removeEventListener("storage", handleStorageUpdate);
    };
  }, []);

  /**
   * Fetch and set user subscription plan on mount or when user changes.
   */
  useEffect(() => {
    const loadUserSubscription = async () => {
      if (!user) {
        try {
          localStorage.setItem("userPlan", "free");
        } catch {
          /* ignore */
        }
        return;
      }

      try {
        const result = await fetchSubscription();

        if (result.success && result.data) {
          const planId = result.data.planId || "";
          const plan = detectTierFromPlanId(planId);
          try {
            localStorage.setItem("userPlan", plan);
            localStorage.setItem("planId", planId);
          } catch {
            // ignore storage errors
          }
        } else {
          try {
            localStorage.setItem("userPlan", "free");
            localStorage.removeItem("planId");
          } catch {
            // ignore storage errors
          }
        }
      } catch (err) {
        try {
          localStorage.setItem("userPlan", "free");
          localStorage.removeItem("planId");
        } catch {
          // ignore storage errors
        }
      }
    };

    loadUserSubscription();
  }, [user]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const handleNeutralHover = (event: React.MouseEvent<HTMLElement>) => {
    event.currentTarget.style.background = "#f1f5f9";
    event.currentTarget.style.color = "#334155";
    event.currentTarget.style.transform = "translateY(-1px)";
  };

  const handleNeutralLeave = (
    event: React.MouseEvent<HTMLElement>,
    isActive = false
  ) => {
    event.currentTarget.style.background = isActive ? "#e2e8f0" : "transparent";
    event.currentTarget.style.color = isActive ? "#334155" : "#64748b";
    event.currentTarget.style.transform = "translateY(0)";
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-left">
          <Link
            to="/project"
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <img src={logo} alt="Logo" className="navbar-logo" />
            <h1 className="navbar-title">Quality for Outcomes</h1>
          </Link>
        </div>

        {user && (
          <div className="navbar-right" ref={menuRef}>
            <Link
              to="/project"
              style={getNavLinkStyle(location.pathname === "/project")}
              onMouseEnter={handleNeutralHover}
              onMouseLeave={(event) =>
                handleNeutralLeave(event, location.pathname === "/project")
              }
              aria-label="Go to project dashboard"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M18,21H6c-1.7,0-3-1.3-3-3V8.8c0-1.1,0.6-2.1,1.5-2.6l6-3.3c0.9-0.5,2-0.5,2.9,0l6,3.3c1,0.5,1.5,1.5,1.5,2.6V18C21,19.7,19.7,21,18,21z"
                  fill="currentColor"
                  opacity=".28"
                />
                <path
                  d="M15,21H9v-6c0-1.1,0.9-2,2-2h2c1.1,0,2,0.9,2,2V21z"
                  fill="currentColor"
                />
              </svg>
            </Link>

            <Link
              to="/profile"
              style={getNavLinkStyle(location.pathname === "/profile")}
              onMouseEnter={handleNeutralHover}
              onMouseLeave={(event) =>
                handleNeutralLeave(event, location.pathname === "/profile")
              }
              aria-label="Go to profile"
              title="Profile"
            >
              <div className="header-profile-avatar">
                {headerProfile?.avatarUrl ? (
                  <img
                    src={headerProfile.avatarUrl}
                    alt="Profile"
                    className="header-profile-avatar-image"
                  />
                ) : (
                  <span>{getInitial(headerProfile, user.email)}</span>
                )}
              </div>
            </Link>

            <div
              style={{
                width: "1px",
                height: "24px",
                background: "#e2e8f0",
                margin: "0 4px",
              }}
            />

            <Link
              to="/logout"
              style={{
                ...getNavLinkStyle(false),
                color: "#ffffff",
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background = "#fef2f2";
                event.currentTarget.style.color = "#b91c1c";
                event.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = "transparent";
                event.currentTarget.style.color = "#ffffff";
                event.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </Link>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;